import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  OperatorFunction,
  catchError,
  combineLatest,
  map,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { NotificationsService } from '../../core/services/notifications.service';
import { CustomerProfilesService } from '../../features/customer-profiles/customer-profiles.service';
import { ProjectsService } from '../../features/projects/projects.service';
import { ErrorService } from '../../core/services/error.service';
import {
  INotificationFeedItem,
  NotificationItemType,
  NotificationTypeFilter,
} from '../../core/types/notification.interface';
import { IProjectList } from '../../core/types/project.interface';
import { DashboardStatCardComponent } from './dashboard-stat-card/dashboard-stat-card.component';
import {
  DashboardRequestKind,
  DashboardRequestRow,
  DashboardRequestRowComponent,
} from './dashboard-request-row/dashboard-request-row.component';

interface DashboardFilter {
  value: NotificationTypeFilter;
  label: string;
}

interface RecentProjectRow {
  id: string;
  name: string;
  subtitle: string;
  timestamp: string;
  link: string[];
}

interface DashboardViewModel {
  openRequests: number | null;
  profilesToReview: number | null;
  pricingRequests: number | null;
  newProjects: number | null;
  recentProjects: RecentProjectRow[];
  feed: DashboardRequestRow[];
  selectedFilter: NotificationTypeFilter;
}

const FEED_LIMIT = 8;
const RECENT_PROJECTS_LIMIT = 4;

@Component({
  selector: 'admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    RouterLink,
    DashboardStatCardComponent,
    DashboardRequestRowComponent,
  ],
})
export class AdminDashboardComponent {
  private readonly notificationsService = inject(NotificationsService);
  private readonly customerProfilesService = inject(CustomerProfilesService);
  private readonly projectsService = inject(ProjectsService);
  private readonly errorService = inject(ErrorService);
  private readonly router = inject(Router);

  readonly filters: DashboardFilter[] = [
    { value: 'all', label: 'All' },
    { value: 'special_pricing_requests', label: 'Pricing' },
    { value: 'customer_profile_requests', label: 'Profiles' },
    { value: 'battery_model_requests', label: 'Batteries' },
  ];

  private readonly selectedFilter$ = new BehaviorSubject<NotificationTypeFilter>('all');

  private readonly openRequests$ = this.notificationsService.activeRequestCount$.pipe(
    startWith(null as number | null),
  );

  private readonly profilesToReview$ = this.customerProfilesService.getAdminQueue({ limit: 1 }).pipe(
    map((response) => response.meta.total),
    take(1),
    this.fallbackTo<number | null>(null),
    startWith(null as number | null),
  );

  private readonly pricingRequests$ = this.notificationsService
    .getNotifications({ notification_type: 'special_pricing_requests', limit: 1 })
    .pipe(
      map((response) => response.meta.total),
      take(1),
      this.fallbackTo<number | null>(null),
      startWith(null as number | null),
    );

  private readonly newProjects$ = this.projectsService
    .findMany({ sort: '-created', limit: 1 })
    .pipe(
      map((response) => response.meta.total),
      take(1),
      this.fallbackTo<number | null>(null),
      startWith(null as number | null),
    );

  private readonly recentProjects$ = this.projectsService
    .findMany({ sort: '-created', limit: RECENT_PROJECTS_LIMIT })
    .pipe(
      map((response) => response.items.map((project) => this.toRecentProject(project))),
      take(1),
      this.fallbackTo<RecentProjectRow[]>([]),
      startWith([] as RecentProjectRow[]),
    );

  private readonly feed$ = this.selectedFilter$.pipe(
    switchMap((notificationType) =>
      this.notificationsService
        .getNotifications({ notification_type: notificationType, limit: FEED_LIMIT })
        .pipe(
          map((response) => response.items.map((item) => this.toRequestRow(item))),
          take(1),
          this.fallbackTo<DashboardRequestRow[]>([]),
          startWith([] as DashboardRequestRow[]),
        ),
    ),
  );

  readonly vm$: Observable<DashboardViewModel> = combineLatest({
    openRequests: this.openRequests$,
    profilesToReview: this.profilesToReview$,
    pricingRequests: this.pricingRequests$,
    newProjects: this.newProjects$,
    recentProjects: this.recentProjects$,
    feed: this.feed$,
    selectedFilter: this.selectedFilter$,
  });

  constructor() {
    this.notificationsService.refreshActiveRequestCount();
  }

  onSelectFilter(filter: NotificationTypeFilter): void {
    this.selectedFilter$.next(filter);
  }

  onOpenRequest(row: DashboardRequestRow): void {
    const target = row.targetLink.trim();
    if (target.startsWith('/') && !target.startsWith('//')) {
      void this.router.navigateByUrl(target);
    }
  }

  /** Toast the error via ErrorService, then keep the dashboard alive with a fallback value. */
  private fallbackTo<T>(fallback: T): OperatorFunction<T, T> {
    return catchError((error: unknown) => {
      this.errorService.handle(error);
      return of(fallback);
    });
  }

  private toRequestRow(item: INotificationFeedItem): DashboardRequestRow {
    const kind = this.requestKind(item.type);
    return {
      id: item.id,
      kind,
      category: this.categoryLabel(kind),
      title: item.title || 'Untitled request',
      subtitle: item.subtitle ?? '',
      timestamp: this.formatRelative(item.created),
      actionLabel: this.actionLabel(kind),
      actionVariant: kind === 'message' || kind === 'project' ? 'ghost' : 'primary',
      targetLink: item.target_link,
    };
  }

  private toRecentProject(project: IProjectList): RecentProjectRow {
    const meta = [project.created_by, project.reseller].filter(Boolean).join(' · ');
    return {
      id: project._id,
      name: project.name,
      subtitle: meta,
      timestamp: this.formatCompact(project.created),
      link: ['/admin', 'projects', project._id],
    };
  }

  private requestKind(type: NotificationItemType | string): DashboardRequestKind {
    switch (type) {
      case 'special_pricing_request':
        return 'pricing';
      case 'customer_profile_request':
        return 'profile';
      case 'battery_model_request':
        return 'battery';
      case 'po_sales_review_request':
      case 'po_support_review_request':
        return 'po';
      case 'unread_message':
        return 'message';
      default:
        return 'project';
    }
  }

  private categoryLabel(kind: DashboardRequestKind): string {
    switch (kind) {
      case 'pricing':
        return 'Pricing request';
      case 'profile':
        return 'Profile review';
      case 'battery':
        return 'Battery model';
      case 'po':
        return 'PO review';
      case 'message':
        return 'Message';
      default:
        return 'New project';
    }
  }

  private actionLabel(kind: DashboardRequestKind): string {
    switch (kind) {
      case 'pricing':
        return 'Review pricing';
      case 'message':
        return 'Open project';
      case 'project':
        return 'View project';
      default:
        return 'Review';
    }
  }

  private formatRelative(iso: string | null): string {
    const minutes = this.minutesSince(iso);
    if (minutes == null) {
      return '';
    }
    if (minutes < 1) {
      return 'Just now';
    }
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    if (days === 1) {
      return 'Yesterday';
    }
    if (days < 7) {
      return `${days}d ago`;
    }
    return new Date(iso as string).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private formatCompact(iso: string | null): string {
    const minutes = this.minutesSince(iso);
    if (minutes == null) {
      return '';
    }
    if (minutes < 1) {
      return 'now';
    }
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days}d`;
    }
    const weeks = Math.floor(days / 7);
    if (weeks < 5) {
      return `${weeks}w`;
    }
    return `${Math.floor(days / 30)}mo`;
  }

  private minutesSince(iso: string | null): number | null {
    if (!iso) {
      return null;
    }
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) {
      return null;
    }
    return Math.floor((Date.now() - then) / 60000);
  }
}
